import { TextField } from "@mui/material";
import FormSelect from "components/elements/form-select/FormSelect";
import { programmingLanguages } from "constants/Constants";
import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Form, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useCookies } from 'react-cookie';
import UploadFile from 'components/courses/add-new-course/steps/UploadFile';
import { getSignedUrl } from "ServerAPI/serverAPI";
import CodeSolutionGenerator from "./CodeSolutionGenerator";
import { generateQNAAPI, getvectorisedResourcesAPI } from 'ServerAPI/serverAPI';
import { useCallback } from "react";
import { Info, ChevronDown, ChevronUp } from "react-feather";
import { Badge } from "react-bootstrap";

const Codesandbox = ({
  handleSubmit = () => { },
  editMode = false,
  activeContent = null,
  savedCourse = null,
  editSavedCourse = () => { },
  courseId = null,
  activeSection = null,
  effectiveCourseId = null, // Added to use prop directly
}) => {
  const [cookies] = useCookies(["userId"]);
  const [languages, setLanguages] = useState([]);
  const [languageVersions, setLanguagesVersions] = useState([]);
  const [codeSandBoxDetails, setCodeSandBoxDetails] = useState({
    problemStatement: "",
    language: "",
    version: "",
    fileRequired: false,
    fileName: "",
    fileUrl: "",
    solution: "",
    harness: "",
    isProctored: false,
    timerMinutes: "",
    marksPerTestCase: 1,
  });
  const [error, setError] = useState("");
  const [pptFileInfo, setPptFileInfo] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [testCases, setTestCases] = useState([{ testInput: "", expectedOutput: "" }]);
  const [showSolutionGenerator, setShowSolutionGenerator] = useState(false);
  const [generateFromContent, setGenerateFromContent] = useState(false);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState("");
  const [difficultyLevel, setDifficultyLevel] = useState("Easy");
  const [additionalContext, setAdditionalContext] = useState("");
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isGeneratingProblem, setIsGeneratingProblem] = useState(false);
  const [bloomTaxonomy, setBloomTaxonomy] = useState("Apply");
  const [marks, setMarks] = useState(10);
  const [outcomeDistribution, setOutcomeDistribution] = useState({});
  const [expandedOutcomes, setExpandedOutcomes] = useState(false);
  const { userId: routeUserId, generatedContentId: initialGeneratedContentId } = location.state || {};
  const userId = routeUserId || cookies.userId; // Prefer route, fallback to cookie
  const [vectorisedResources, setVectorisedResources] = useState([]);
  // Log props on component mount and update
  useEffect(() => {
    console.log("Codesandbox - Props received:", {
      editMode,
      courseId,
      savedCourseId: savedCourse?.courseId,
      effectiveCourseId,
      activeSectionId: activeSection?.sectionId,
      activeContent,
      userId: cookies?.userId,
    });
  }, [editMode, courseId, savedCourse, effectiveCourseId, activeSection, cookies?.userId]);

  // Initialize languages dropdown
  useEffect(() => {
    const formattedLanguages = programmingLanguages.map((language) => ({
      value: language.title,
      label: language.title,
    }));
    setLanguages(formattedLanguages);
  }, []);

  // Initialize edit mode details
  useEffect(() => {
    if (editMode && activeContent) {
      const fileRequired = !!activeContent.fileRequired;
      const fileName = activeContent.fileName || "";
      const fileUrl = activeContent.fileUrl || "";
      setCodeSandBoxDetails({
        problemStatement: activeContent.problemStatement || "",
        language: activeContent.language || "",
        version: activeContent.version || "",
        fileRequired,
        fileName,
        fileUrl,
        solution: activeContent.solution || "",
        harness: activeContent.harness || "",
        isProctored: activeContent.isProctored || false,
        timerMinutes: activeContent.timerMinutes || "",
        marksPerTestCase: activeContent.marksPerTestCase ?? 1,
      });
      setTestCases(activeContent.testCases && activeContent.testCases.length > 0
        ? activeContent.testCases
        : [{ testInput: "", expectedOutput: "" }]);
      if (fileRequired && fileName) {
        setPptFileInfo({ name: fileName, url: fileUrl || undefined });
      } else {
        setPptFileInfo(null);
      }
      console.log("Codesandbox - Edit mode initialized:", {
        fileRequired,
        fileName,
        fileUrl,
        pptFileInfo: { name: fileName, url: fileUrl || undefined },
        activeContent,
      });
    }
    // Only re-initialize when switching to a different saved content item, not on every
    // re-render that produces a new activeContent object for the same content (which was
    // wiping out in-progress, unsaved test case edits).
  }, [editMode, activeContent?.contentId]);

  useEffect(() => {
    if (generateFromContent && activeSection?.sectionId) {
      fetchVectorisedResources();
    } else {
      setAvailableFiles([]);
      setSelectedFileId("");
    }
  }, [generateFromContent, activeSection?.sectionId]);

  const updateOutcomeDistribution = (co, value) => {
    setOutcomeDistribution(prev => {
      const newDist = { ...prev };
      if (value === 0 || value === "") {
        delete newDist[co];
      } else {
        newDist[co] = parseInt(value) || 0;
      }
      return newDist;
    });
  };

  // === FETCH VECTORIZED FILES ===
  const fetchVectorisedResources = useCallback(async () => {
    if (!cookies.userId || !activeSection?.sectionId) return;

    try {
      setIsLoadingFiles(true);
      const response = await getvectorisedResourcesAPI(cookies.userId, activeSection?.sectionId);
      const rawData = response.data;

      console.log("Vectorised resources response:", rawData);

      // Process the response to extract all vectorized files
      const allVectorizedFiles = [];

      // Extract from videoContents
      if (rawData.videoContents && Array.isArray(rawData.videoContents)) {
        rawData.videoContents.forEach(video => {
          if (video.vectorSpace && video.contentFileName) {
            allVectorizedFiles.push({
              id: video.contentId,
              name: video.contentFileName,
              type: 'video'
            });
          }
        });
      }

      // Extract from supplementaryMaterials
      if (rawData.supplementaryMaterials && Array.isArray(rawData.supplementaryMaterials)) {
        rawData.supplementaryMaterials.forEach(material => {
          if (material.vectorSpace && material.contentFileName) {
            allVectorizedFiles.push({
              id: material.contentId,
              name: material.contentFileName,
              type: 'material'
            });
          }
        });
      }

      // Also handle case where response is a direct array
      if (Array.isArray(rawData)) {
        rawData.forEach(item => {
          if (item.vectorSpace && item.contentFileName) {
            allVectorizedFiles.push({
              id: item.contentId || item.id,
              name: item.contentFileName,
              type: item.contentType || 'unknown'
            });
          }
        });
      }

      console.log("Processed vectorized files:", allVectorizedFiles);
      setAvailableFiles(allVectorizedFiles);
      setVectorisedResources(rawData);

    } catch (err) {
      console.error("Failed to load vectorised resources:", err);
      setAvailableFiles([]);
      setVectorisedResources([]);
    } finally {
      setIsLoadingFiles(false);
    }
  }, [cookies.userId, activeSection?.sectionId]);

  const handleGenerateQA = async () => {
    if (!selectedFileId) {
      setError("Please select a file");
      return;
    }
    if (!difficultyLevel) {
      setError("Please select difficulty level");
      return;
    }

    try {
      setIsGenerating(true);
      setError("");

      const selectedResourceFile = availableFiles.find(file => file.id === selectedFileId);
      if (!selectedResourceFile) {
        setError("Please select a valid file");
        return;
      }

      // Updated payload structure matching new backend requirements
      const payload = {
        pdf_filename: selectedResourceFile.name,
        org_id: savedCourse?.orgId || "",
        domain: savedCourse?.courseDomain || "Computer Science",
        department: savedCourse?.courseDepartment || "IT",
        mandatory: [
          {
            question_type: "programming",
            total_questions: 1,
            marks_per_question: Number(marks),
            bloom_taxonomy: bloomTaxonomy,
            language: codeSandBoxDetails.language || "Python",
            course_outcome_distribution: Object.entries(outcomeDistribution)
              .filter(([_, count]) => Number(count) > 0)
              .reduce((acc, [co, count]) => ({ ...acc, [co]: Number(count) }), {})
          }
        ]
      };

      console.log("Sending payload to generateQNAAPI:", payload);
      const response = await generateQNAAPI(payload);

      if (response.data) {
        console.log("Full API response:", response.data);

        // Extract the programming question from the response
        let generatedStatement = "";
        const resultData = response.data?.data || response.data;

        // Check for programming or programming_questions key
        const progQuestions = resultData.programming || resultData.programming_questions || [];

        if (Array.isArray(progQuestions) && progQuestions.length > 0) {
          generatedStatement = progQuestions[0].question || progQuestions[0].problem_statement || "";
        } else {
          // Fallback: try to find any question in any list
          const anyKey = Object.keys(resultData).find(key => Array.isArray(resultData[key]) && resultData[key].length > 0);
          if (anyKey) {
            generatedStatement = resultData[anyKey][0].question || "";
          }
        }

        if (generatedStatement) {
          setCodeSandBoxDetails(prev => ({
            ...prev,
            problemStatement: generatedStatement
          }));
          setError("");
        } else {
          setError("No problem statement was generated. Please try again with different settings.");
        }

      } else {
        setError("Problem statement generation failed - no data received");
      }
    } catch (error) {
      console.error('Error generating problem statement:', error);
      setError(error.response?.data?.message || error.message || 'Failed to generate problem statement');
    } finally {
      setIsGenerating(false);
    }
  };
  // Update language versions & reset fields only when language changes
  useEffect(() => {
    const result = programmingLanguages.filter(
      (language) => language.title === codeSandBoxDetails.language
    );
    setLanguagesVersions(
      result[0]?.versions.map((version) => ({
        value: version,
        label: version,
      })) || []
    );

    // Reset fields only if language changed and not matching activeContent in edit mode
    if (codeSandBoxDetails.language && codeSandBoxDetails.language !== (activeContent?.language || "")) {
      setCodeSandBoxDetails((prev) => ({
        ...prev,
        fileRequired: false,
        fileName: "",
        fileUrl: "",
        solution: "",
        harness: "",
        version: "",
      }));
      setPptFileInfo(null);
      console.log("Codesandbox - Language changed, resetting fields:", {
        newLanguage: codeSandBoxDetails.language,
        previousLanguage: activeContent?.language,
      });
    }
  }, [codeSandBoxDetails.language, activeContent]);

  const getDefaultHarness = (language) => {
    const lang = (language || "").toLowerCase().trim();
    if (lang === "python") {
      return `obj = Solution()\ns = input()\nprint(obj.is_accepted(s))`;
    } else if (lang === "java") {
      return `import java.util.Scanner;\n\npublic class Main {\n    public static void main(String[] args) {\n        Scanner sc = new Scanner(System.in);\n        Solution sol = new Solution();\n        System.out.println(sol.isAccepted(sc.next()));\n    }\n}`;
    } else if (lang === "c++" || lang === "c") {
      return `#include <iostream>\n#include <string>\nusing namespace std;\n\nint main() {\n    string s;\n    cin >> s;\n    Solution sol;\n    cout << sol.isAccepted(s) << endl;\n    return 0;\n}`;
    } else if (lang === "c#") {
      return `using System;\n\npublic class Program {\n    public static void Main() {\n        Solution sol = new Solution();\n        string s = Console.ReadLine();\n        Console.WriteLine(sol.IsAccepted(s));\n    }\n}`;
    } else if (lang === "mysql") {
      return `/* Sample MySQL Harness */\nSELECT * FROM table_name WHERE condition;`;
    }
    return "";
  };

  const handleOnChange = (e) => {
    const { value, name } = e.target;
    setCodeSandBoxDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileRequiredChange = (e) => {
    const { value } = e.target;
    const isRequired = value === "yes";
    setCodeSandBoxDetails((prev) => ({
      ...prev,
      fileRequired: isRequired,
      fileName: isRequired ? prev.fileName : "",
      fileUrl: isRequired ? prev.fileUrl : "",
    }));
    if (!isRequired) {
      setPptFileInfo(null);
    }
    console.log("Codesandbox - File required changed:", { isRequired, codeSandBoxDetails });
  };

  const handleFileChange = (fileInfo) => {
    const actualFile = fileInfo?.target?.files?.[0] || fileInfo;
    setPptFileInfo(actualFile);
    setCodeSandBoxDetails((prev) => ({
      ...prev,
      fileName: actualFile && actualFile.name ? actualFile.name : "",
      fileUrl: actualFile?.url || prev.fileUrl,
    }));
    console.log("Codesandbox - File selected:", { fileInfo: actualFile, fileName: actualFile?.name, fileUrl: actualFile?.url });
  };

  const handleTestCaseChange = (index, e) => {
    const { name, value } = e.target;
    setTestCases((prev) =>
      prev.map((tc, i) => (i === index ? { ...tc, [name]: value } : tc))
    );
  };

  const addTestCase = () => setTestCases([...testCases, { testInput: "", expectedOutput: "" }]);
  const removeTestCase = (index) => setTestCases(testCases.filter((_, i) => i !== index));

  const uploadFile = async () => {
    console.log("Codesandbox - uploadFile called:", {
      effectiveCourseId,
      sectionId: activeSection?.sectionId,
      fileName: codeSandBoxDetails.fileName,
      pptFileInfo,
      userId: cookies?.userId,
      method: editMode && codeSandBoxDetails.fileUrl ? "MODIFY" : "PUT",
    });

    if (!effectiveCourseId) {
      setError("Invalid course ID");
      console.error("Codesandbox - uploadFile failed: Invalid course ID", { effectiveCourseId });
      return false;
    }
    if (!activeSection?.sectionId) {
      setError("Invalid section ID");
      console.error("Codesandbox - uploadFile failed: Invalid section ID", { activeSection });
      return false;
    }
    if (!pptFileInfo || !codeSandBoxDetails.fileName) {
      setError("File and file name are required for upload");
      console.error("Codesandbox - uploadFile failed: Missing file or fileName", { pptFileInfo, fileName: codeSandBoxDetails.fileName });
      return false;
    }

    const fileName = codeSandBoxDetails.fileName.trim();
    const selectedExt = pptFileInfo?.name?.match(/\.([^/.]+)$/)?.[1]?.toLowerCase();
    if (!selectedExt) {
      setError("Uploaded file must have a valid extension");
      console.error("Codesandbox - uploadFile failed: Invalid file extension", { fileName: pptFileInfo?.name });
      return false;
    }
    if (codeSandBoxDetails.language === "MySQL" && selectedExt !== "sql") {
      setError("Only .sql files are allowed for MySQL schema uploads");
      console.error("Codesandbox - uploadFile failed: Invalid file type for MySQL", { selectedExt });
      return false;
    }
    if (fileName !== pptFileInfo.name) {
      setError("File name must match the uploaded file's name");
      console.error("Codesandbox - uploadFile failed: File name mismatch", { fileName, pptFileName: pptFileInfo.name });
      return false;
    }

    try {
      setIsGenerating(true);
      const payload = {
        courseId: effectiveCourseId,
        sectionId: activeSection.sectionId,
        method: editMode && codeSandBoxDetails.fileUrl ? "MODIFY" : "PUT",
        userId: cookies?.userId || "",
        fileName,
        existingFileName: editMode ? codeSandBoxDetails.fileName : undefined,
      };
      console.log("Codesandbox - getSignedUrl payload:", payload);

      const response = await getSignedUrl(payload);
      console.log("Codesandbox - getSignedUrl response:", response.data);

      if (response.data?.preSignedUrl) {
        const uploadResponse = await fetch(response.data.preSignedUrl, {
          method: 'PUT',
          body: pptFileInfo,
        });

        if (!uploadResponse.ok) {
          console.error("Codesandbox - File upload failed:", { status: uploadResponse.status, statusText: uploadResponse.statusText });
          throw new Error(`Upload failed: ${uploadResponse.statusText}`);
        }

        const updatedDetails = {
          ...codeSandBoxDetails,
          fileName,
          fileUrl: response.data.objectUrl,
        };
        setCodeSandBoxDetails(updatedDetails);
        setPptFileInfo({ name: fileName, url: response.data.objectUrl });
        console.log("Codesandbox - File uploaded successfully:", { updatedDetails, newPptFileInfo: { name: fileName, url: response.data.objectUrl } });

        if (editMode && typeof editSavedCourse === 'function') {
          const updatedCourseData = {
            ...savedCourse,
            sections: savedCourse.sections.map(section =>
              section.sectionId === activeSection.sectionId
                ? {
                  ...section,
                  content: section.content.map(content =>
                    content.contentNumber === activeContent?.contentNumber
                      ? { ...content, fileName, fileUrl: response.data.objectUrl }
                      : content
                  ),
                }
                : section
            ),
          };
          console.log("Codesandbox - Updating saved course:", { updatedCourseData });
          await editSavedCourse(updatedCourseData);
        }

        return true;
      } else {
        throw new Error('No signed URL received from server');
      }
    } catch (error) {
      setError(error.message || 'Error uploading file');
      console.error("Codesandbox - uploadFile error:", { error: error.message, stack: error.stack });
      return false;
    } finally {
      setIsGenerating(false);
    }
  };

  const submitStatement = async () => {
    console.log("Codesandbox - submitStatement called:", { codeSandBoxDetails, testCases, pptFileInfo });
    if (!codeSandBoxDetails.language) {
      setError("Language is required");
      console.error("Codesandbox - submitStatement failed: Language missing");
      return;
    }
    if (!codeSandBoxDetails.version) {
      setError("Version is required");
      console.error("Codesandbox - submitStatement failed: Version missing");
      return;
    }
    if (!codeSandBoxDetails.problemStatement) {
      setError("Problem statement is required");
      console.error("Codesandbox - submitStatement failed: Problem statement missing");
      return;
    }
    if (codeSandBoxDetails.fileRequired && !codeSandBoxDetails.fileName) {
      setError("File name is required");
      console.error("Codesandbox - submitStatement failed: File name missing");
      return;
    }
    if (codeSandBoxDetails.fileRequired && !(codeSandBoxDetails.fileUrl || pptFileInfo)) {
      setError("Please upload a file");
      console.error("Codesandbox - submitStatement failed: File missing");
      return;
    }

    if (testCases.length === 0) {
      setError("Please add at least one test case");
      console.error("Codesandbox - submitStatement failed: No test cases");
      return;
    }

    // Blank input/output is a valid test case (e.g. reversing an empty string should
    // return an empty string) — every row the instructor kept via Add/Remove is saved as-is,
    // nothing is filtered out based on field content.
    const needsUpload = codeSandBoxDetails.fileRequired && pptFileInfo instanceof File;
    if (needsUpload) {
      console.log("Codesandbox - submitStatement: Uploading file before submission");
      const success = await uploadFile();
      if (!success) {
        console.error("Codesandbox - submitStatement: File upload failed, aborting submission");
        return;
      }
    }

    const detailsToSubmit = {
      ...codeSandBoxDetails,
      testCases,
    };
    console.log("Codesandbox - Submitting details:", detailsToSubmit);

    setError("");
    handleSubmit(detailsToSubmit);
  };

  const handleGenerateSolution = () => {
    if (!codeSandBoxDetails.problemStatement || !codeSandBoxDetails.language) {
      setError("Please provide both a problem statement and a language before generating a solution.");
      console.error("Codesandbox - handleGenerateSolution failed:", {
        problemStatement: codeSandBoxDetails.problemStatement,
        language: codeSandBoxDetails.language,
      });
      return;
    }
    setShowSolutionGenerator(true);
  };

  const handleSolutionAccept = ({ solution, harness, testCases: generatedTestCases, schema, schemaFileName }) => {
    setCodeSandBoxDetails((prev) => ({
      ...prev,
      solution: solution || "",
      harness: harness || "",
      fileRequired: schema ? true : prev.fileRequired,
      fileName: schemaFileName ? schemaFileName : prev.fileName,
    }));
    if (schema && schemaFileName) {
      const newFile = new File([schema], schemaFileName, { type: "text/plain" });
      setPptFileInfo(newFile);
    }
    setTestCases(generatedTestCases.length > 0 ? generatedTestCases : [{ testInput: "", expectedOutput: "" }]);
    setShowSolutionGenerator(false);
    console.log("Codesandbox - Solution accepted:", { solution, generatedTestCases, schema, schemaFileName });
  };

  return (
    <Card style={{ borderColor: '#23479f' }}>
      <Card.Body>
        <div>
          <FormSelect
            options={languages}
            name="language"
            placeholder="Select Language"
            onChange={handleOnChange}
            value={codeSandBoxDetails?.language}
          />
        </div>
        <div className="mt-3">
          <FormSelect
            options={languageVersions}
            name="version"
            placeholder="Select Version"
            onChange={handleOnChange}
            value={codeSandBoxDetails?.version}
          />
        </div>
        {/* === PROCTORING TOGGLE === */}
        <div className="mt-3 p-3 border rounded d-flex align-items-center justify-content-between" style={{ backgroundColor: codeSandBoxDetails.isProctored ? '#fff8e1' : '#f8f9fa', borderColor: codeSandBoxDetails.isProctored ? '#f59e0b' : '#dee2e6' }}>
          <div>
            <Form.Check
              type="switch"
              id="code-proctoring"
              label={<span className="fw-semibold">Enable Proctoring</span>}
              checked={codeSandBoxDetails.isProctored}
              onChange={(e) => setCodeSandBoxDetails(prev => ({ ...prev, isProctored: e.target.checked }))}
            />
            <div className="text-muted small mt-1">
              Tracks idle time, activity, and paste events during the coding session
            </div>
          </div>
          {codeSandBoxDetails.isProctored && (
            <Badge bg="warning" text="dark" className="ms-3 px-3 py-2">Proctored</Badge>
          )}
        </div>

        {/* === TIMER + MARKS (only when proctoring is enabled) === */}
        {codeSandBoxDetails.isProctored && (
          <div className="mt-2 p-3 border rounded" style={{ backgroundColor: '#fffbeb', borderColor: '#f59e0b' }}>
            <Row className="g-3">
              <Col xs={12} sm={6}>
                <Form.Label className="fw-semibold small mb-1">Session Timer (minutes)</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  placeholder="e.g. 60"
                  value={codeSandBoxDetails.timerMinutes}
                  onChange={(e) => setCodeSandBoxDetails(prev => ({ ...prev, timerMinutes: e.target.value }))}
                  style={{ borderColor: '#f59e0b' }}
                />
                <Form.Text className="text-muted">
                  Student will be auto-submitted when time runs out.
                </Form.Text>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Label className="fw-semibold small mb-1">Marks per Test Case</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  placeholder="e.g. 1"
                  value={codeSandBoxDetails.marksPerTestCase}
                  onChange={(e) => setCodeSandBoxDetails(prev => ({ ...prev, marksPerTestCase: Math.max(1, parseInt(e.target.value) || 1) }))}
                  style={{ borderColor: '#f59e0b' }}
                />
                <Form.Text className="text-muted">
                  All test cases carry equal marks. Total = marks × no. of test cases.
                </Form.Text>
              </Col>
            </Row>
          </div>
        )}

        {/* === TOGGLE: GENERATE PROBLEM STATEMENT FROM CONTENT === */}
        <div className="mt-3 d-flex align-items-center gap-3">
          <Form.Check
            type="switch"
            id="generate-from-content"
            label="Generate Problem Statement from Section Content"
            checked={generateFromContent}
            onChange={(e) => {
              setGenerateFromContent(e.target.checked);
              if (!e.target.checked) {
                setCodeSandBoxDetails(prev => ({ ...prev, problemStatement: "" }));
              }
            }}
          />
        </div>

        {/* === FILE SELECTION + OPTIONS (WHEN TOGGLED) === */}
        {generateFromContent && (
          <div className="mt-3 p-3 border rounded" style={{ backgroundColor: "#f8f9fa" }}>
            <Form.Label className="fw-semibold">Select Resource File</Form.Label>
            {isLoadingFiles ? (
              <div className="text-center p-2">
                <span className="spinner-border spinner-border-sm me-2"></span>
                Loading files...
              </div>
            ) : availableFiles.length > 0 ? (
              <Form.Select
                value={selectedFileId}
                onChange={(e) => setSelectedFileId(e.target.value)}
                className="mb-3"
              >
                <option value="">-- Select a file --</option>
                {availableFiles.map(file => (
                  <option key={file.id} value={file.id}>
                    {file.name}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <p className="text-muted small">No vectorized files found in this section.</p>
            )}
            {/* === DIFFICULTY, TAXONOMY & MARKS === */}
            <Row className="g-3 mb-3">
              {/* <Col md={4}>
                <Form.Label className="fw-semibold small">Difficulty</Form.Label>
                <Form.Select
                  size="sm"
                  value={difficultyLevel}
                  onChange={(e) => setDifficultyLevel(e.target.value)}
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </Form.Select>
              </Col> */}
              <Col md={4}>
                <Form.Label className="fw-semibold small">Bloom's Taxonomy</Form.Label>
                <Form.Select
                  size="sm"
                  value={bloomTaxonomy}
                  onChange={(e) => setBloomTaxonomy(e.target.value)}
                >
                  {["Remember", "Understand", "Apply", "Analyze", "Evaluate", "Create"].map(tax => (
                    <option key={tax} value={tax}>{tax}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label className="fw-semibold small">Marks</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  value={marks}
                  onChange={(e) => setMarks(e.target.value)}
                />
              </Col>
            </Row>

            {/* === COURSE OUTCOME DISTRIBUTION === */}
            {savedCourse?.learningOutcome?.length > 0 && (
              <div className="mt-3 pt-3 border-top border-dashed">
                <div
                  className="d-flex align-items-center justify-content-between mb-3"
                  onClick={() => setExpandedOutcomes(!expandedOutcomes)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <h6 className="text-primary fw-bold mb-0 small">COURSE OUTCOME DISTRIBUTION</h6>
                    <div className="text-muted">
                      {expandedOutcomes ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </div>
                  <Badge
                    bg={Object.values(outcomeDistribution).reduce((a, b) => a + b, 0) === 1 ? "success" : "warning"}
                    className="opacity-75"
                  >
                    {Object.values(outcomeDistribution).reduce((a, b) => a + b, 0)} / 1 Assigned
                  </Badge>
                </div>

                {expandedOutcomes && (
                  <div className="d-flex flex-column gap-2">
                    {savedCourse.learningOutcome.map((co, idx) => (
                      <div key={idx} className="d-flex align-items-center bg-white p-2 rounded border shadow-sm" style={{ minWidth: 0 }}>
                        <div className="flex-grow-1 min-w-0 me-3">
                          <div className="small text-dark fw-medium text-truncate" title={co} style={{ fontSize: '11px' }}>
                            {co}
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="p-0 d-flex align-items-center justify-content-center"
                            style={{ width: '24px', height: '24px', borderRadius: '4px' }}
                            onClick={() => updateOutcomeDistribution(co, Math.max(0, (outcomeDistribution[co] || 0) - 1))}
                          >
                            -
                          </Button>
                          <Form.Control
                            type="number"
                            size="sm"
                            style={{ width: '35px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px' }}
                            className="bg-light border-0 px-1"
                            value={outcomeDistribution[co] || 0}
                            onChange={(e) => updateOutcomeDistribution(co, e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            min="0"
                            max="1"
                          />
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="p-0 d-flex align-items-center justify-content-center"
                            style={{ width: '24px', height: '24px', borderRadius: '4px' }}
                            onClick={() => updateOutcomeDistribution(co, Math.min(1, (outcomeDistribution[co] || 0) + 1))}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    ))}
                    {Object.values(outcomeDistribution).reduce((a, b) => a + b, 0) !== 1 && (
                      <p className="text-warning x-small mt-1 mb-0" style={{ fontSize: '10px' }}>
                        <Info size={10} className="me-1" />
                        Assign 1 question to an outcome.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}


            <Button
              size="sm"
              className="w-100"
              onClick={handleGenerateQA}
              disabled={isGenerating || !selectedFileId || !difficultyLevel}
              style={{ backgroundColor: '#23479f', borderColor: '#23479f' }}
            >
              {isGenerating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Generating...
                </>
              ) : "Generate Problem Statement"}
            </Button>
          </div>
        )}
        <div className="mt-3">
          <Form.Label className="fw-semibold">Problem Statement</Form.Label>
          <Form.Control
            as="textarea"
            rows={6}
            placeholder="Write the problem statement"
            id="problemStatement"
            name="problemStatement"
            value={codeSandBoxDetails?.problemStatement}
            onChange={handleOnChange}
            required
            style={{ borderColor: '#23479f' }}
          />
        </div>
        {codeSandBoxDetails.solution && (
          <div className="mt-3">
            <Form.Label className="fw-semibold">Generated Solution</Form.Label>
            <pre
              style={{
                background: '#f8f9fa',
                padding: '15px',
                borderRadius: '5px',
                border: '1px solid #23479f',
                fontSize: '0.95rem',
                whiteSpace: 'pre-wrap'
              }}
            >
              {codeSandBoxDetails.solution}
            </pre>
          </div>
        )}
        <div className="mt-3">
          <Form.Label className="fw-semibold">
            {codeSandBoxDetails.language === "MySQL" ? "Is a schema file required?" : "Is a file required?"}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="file-required-tooltip">
                  <div className="text-start">
                    <strong>File Operations:</strong> Enable this if your program requires reading from or writing to an external file.
                    <br /><br />
                    <strong>Example:</strong> Processing a CSV of student records or reading data from a text file.
                    <br /><br />
                    <strong>Supported:</strong> PDF, TXT, CSV, XLSX, DOCX.
                  </div>
                </Tooltip>
              }
            >
              <span>
                <Info size={14} className="ms-1 text-muted" style={{ cursor: 'pointer' }} />
              </span>
            </OverlayTrigger>
          </Form.Label>
          <FormSelect
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
            name="fileRequired"
            placeholder={codeSandBoxDetails.language === "MySQL" ? "Select if schema file is required" : "Select if file is required"}
            onChange={handleFileRequiredChange}
            value={codeSandBoxDetails.fileRequired === true ? "yes" : "no"}
          />
        </div>
        {codeSandBoxDetails.fileRequired && (
          <div className="mt-3">
            <div>
              <Form.Label className="fw-semibold">
                {codeSandBoxDetails.language === "MySQL" ? "Upload Schema File" : "Upload File"}
              </Form.Label>
              <UploadFile
                fileType={codeSandBoxDetails.language === "MySQL" ? "sql" : "ppt,txt,pdf,xlsx,docx,json,csv"}
                handleFileChange={handleFileChange}
                showFileInfo={true}
                selectedFile={pptFileInfo}
                disabled={isGenerating}
              />
              <Form.Text className="text-muted">
                {codeSandBoxDetails.language === "MySQL"
                  ? "Upload a schema file (sql)"
                  : "Upload a file (ppt, txt, pdf, csv, xlsx, docx, json)"}
              </Form.Text>
              <Button
                className="mt-2"
                onClick={uploadFile}
                disabled={isGenerating || !pptFileInfo || (!!codeSandBoxDetails.fileUrl && !(pptFileInfo instanceof File))}
                style={{
                  backgroundColor: (isGenerating || !pptFileInfo || (!!codeSandBoxDetails.fileUrl && !(pptFileInfo instanceof File))) ? '#6c757d' : '#23479f',
                  borderColor: (isGenerating || !pptFileInfo || (!!codeSandBoxDetails.fileUrl && !(pptFileInfo instanceof File))) ? '#6c757d' : '#23479f'
                }}
              >
                {isGenerating
                  ? "Uploading..."
                  : (!!codeSandBoxDetails.fileUrl && !(pptFileInfo instanceof File))
                    ? "Uploaded Successfully ✓"
                    : codeSandBoxDetails.language === "MySQL"
                      ? "Upload Schema File"
                      : "Upload File"}
              </Button>
            </div>
            <div className="mt-3">
              <Form.Label className="fw-semibold">
                {codeSandBoxDetails.language === "MySQL" ? "Schema File Name" : "File Name"}
              </Form.Label>
              <Form.Control
                type="text"
                name="fileName"
                placeholder={
                  codeSandBoxDetails.language === "MySQL"
                    ? "Schema file name will be set after selecting a file"
                    : "File name will be set after selecting a file"
                }
                value={codeSandBoxDetails.fileName}
                readOnly
                style={{ borderColor: '#23479f' }}
              />
            </div>
          </div>
        )}
        {/* <div className="mt-3">
          <Form.Label className="fw-semibold">
            Harness Function
            <OverlayTrigger
              key={`harness-trigger-${codeSandBoxDetails.language}`}
              placement="top"
              overlay={
                <Tooltip id="harness-tooltip">
                  <div className="text-start">
                    <strong>Driver Code:</strong> The harness 'drives' your solution by reading input, calling your function, and printing the result for verification.
                    <br /><br />
                    <strong>Example ({codeSandBoxDetails.language || "Python"}):</strong>
                    <br />
                    <pre style={{ margin: 0, padding: '5px', background: '#2d2d2d', color: '#fff', borderRadius: '4px', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                      {(() => {
                        const lang = (codeSandBoxDetails.language || "").toLowerCase().trim();
                        if (lang === "python") {
                          return `obj = Solution()\ns = input()\nprint(obj.is_accepted(s))`;
                        } else if (lang === "java") {
                          return `public static void main(String[] args) {\n  Scanner sc = new Scanner(System.in);\n  Solution sol = new Solution();\n  System.out.println(sol.isAccepted(sc.next()));\n}`;
                        } else if (lang === "c++" || lang === "c") {
                          return `int main() {\n  string s;\n  cin >> s;\n  Solution sol;\n  cout << sol.isAccepted(s) << endl;\n  return 0;\n}`;
                        } else if (lang === "c#") {
                          return `public static void Main() {\n  Solution sol = new Solution();\n  string s = Console.ReadLine();\n  Console.WriteLine(sol.IsAccepted(s));\n}`;
                        } else if (lang === "mysql") {
                          return `SELECT * FROM table_name\nWHERE condition;`;
                        }
                        return `// Sample harness code for ${codeSandBoxDetails.language}`;
                      })()}
                    </pre>
                  </div>
                </Tooltip>
              }
            >
              <span>
                <Info size={14} className="ms-1 text-muted" style={{ cursor: 'pointer' }} />
              </span>
            </OverlayTrigger>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            placeholder={getDefaultHarness(codeSandBoxDetails.language) || "Write the harness function here"}
            name="harness"
            value={codeSandBoxDetails?.harness || ""}
            onChange={handleOnChange}
            style={{ borderColor: '#23479f' }}
          />
        </div> */}
        <div className="mt-3">
          <h5 className="fw-semibold">
            Test Cases
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="testcases-tooltip">
                  <div className="text-start">
                    <strong>Validation:</strong> Use test cases to verify your logic. Input is what your code reads, and Expected Output is what it should print.
                    <br /><br />
                    <strong>Example:</strong>
                    <br />
                    Input: <code>"ab"</code> → Output: <code>"true"</code>
                    <br />
                    Input: <code>"ba"</code> → Output: <code>"false"</code>
                  </div>
                </Tooltip>
              }
            >
              <span>
                <Info size={14} className="ms-1 text-muted" style={{ cursor: 'pointer' }} />
              </span>
            </OverlayTrigger>
          </h5>
          {testCases.map((testCase, index) => (
            <div key={index} style={{ marginBottom: "10px", border: "1px solid #23479f", padding: "10px", borderRadius: '5px' }}>
              <Form.Control
                type="text"
                name="testInput"
                placeholder="Test Input (e.g., 5 10)"
                value={testCase.testInput}
                onChange={(e) => handleTestCaseChange(index, e)}
                className="mb-2"
                style={{ borderColor: '#23479f' }}
              />
              <Form.Control
                type="text"
                name="expectedOutput"
                placeholder="Expected Output (e.g., 15)"
                value={testCase.expectedOutput}
                onChange={(e) => handleTestCaseChange(index, e)}
                style={{ borderColor: '#23479f' }}
              />
              {testCases.length > 1 && (
                <Button
                  variant="danger"
                  className="mt-2"
                  onClick={() => removeTestCase(index)}
                  style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
          <Button
            variant="success"
            className="mt-2"
            onClick={addTestCase}
            style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
          >
            + Add Test Case
          </Button>
        </div>
        {error && <div className="text-danger mt-2">{error}</div>}
        <div className="mt-3 d-flex justify-content-between">
          <Button
            onClick={handleGenerateSolution}
            disabled={!codeSandBoxDetails.language || !codeSandBoxDetails.problemStatement}
            style={{ backgroundColor: '#23479f', borderColor: '#23479f', marginRight: '10px' }}
          >
            {codeSandBoxDetails.language?.toLowerCase() === 'mysql' ? 'Generate Solution, Schema and Test Cases' : 'Generate Solution and Test Cases'}
          </Button>
          <Button
            onClick={submitStatement}
            style={{ backgroundColor: '#23479f', borderColor: '#23479f' }}
          >
            Save
          </Button>
        </div>
        {showSolutionGenerator && (
          <CodeSolutionGenerator
            problemStatement={codeSandBoxDetails.problemStatement}
            language={codeSandBoxDetails.language}
            version={codeSandBoxDetails.version}
            testCases={testCases}
            userId={cookies?.userId || ""}
            fileUrl={codeSandBoxDetails.fileUrl}
            requestMode={"all"}
            bloomLevel={bloomTaxonomy}
            onAccept={handleSolutionAccept}
            onClose={() => setShowSolutionGenerator(false)}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default Codesandbox;